import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { toast } from 'sonner';
import { authApi, onAuthInvalid } from '@/lib/api';
import { 
  updateLastActivity, 
  clearSessionData,
  forceClearAuthCookies,
  rateLimitManager,
} from '@/lib/security';
import { 
  getSessionConfig, 
  getCachedSessionConfig, 
  shouldRefreshSessionConfig,
  type SessionConfig 
} from '@/lib/session-config';
import { getAdminSessionManager } from '@/lib/admin-session-manager';
import config from '@/config';
import { ADMIN_LOGIN_PATH, isAdminLoginPath } from '@/lib/auth-paths';


// Session configuration is now loaded dynamically from backend
// Fallback values are handled in session-config.ts

type User = {
  _id: string;
  accountId: string;
  email: string;
  fullName: string;
  role?: 'admin' | 'support' | 'user';
};

export type AdminLoginResult =
  | { status: 'success' }
  | {
      status: 'requires2fa';
      challengeId: string;
      maskedEmail: string;
      expiresInMinutes: number;
    }
  | { status: 'error' };

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AdminLoginResult>;
  verifyLogin2fa: (challengeId: string, code: string) => Promise<boolean>;
  resendLogin2faCode: (challengeId: string) => Promise<{
    challengeId: string;
    maskedEmail: string;
    expiresInMinutes: number;
  } | null>;
  logout: () => Promise<boolean>;
  extendSession: () => void;
  isLoading: boolean;
  isBackendOffline: boolean;
  isFormActive: boolean;
  setFormActive: (active: boolean) => void;
  sessionConfig: SessionConfig;
  loadSessionConfig: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: async () => ({ status: 'error' }),
  verifyLogin2fa: async () => false,
  resendLogin2faCode: async () => null,
  logout: async () => false,
  extendSession: () => {},
  isLoading: true,
  isBackendOffline: false,
  isFormActive: false,
  setFormActive: () => {},
  sessionConfig: getCachedSessionConfig(),
  loadSessionConfig: async () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context;
};

// Simple authentication state machine
type AuthState = 'idle' | 'logging_in' | 'logging_out' | 'restoring' | 'refreshing';

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBackendOffline, setIsBackendOffline] = useState(false);
  const [authState, setAuthState] = useState<AuthState>('idle');
  const [hasAttemptedRestoration, setHasAttemptedRestoration] = useState(false);
  const [isFormActive, setIsFormActive] = useState(false);
  const [sessionConfig, setSessionConfig] = useState<SessionConfig>(getCachedSessionConfig());
  
  // Initialize cross-tab session manager
  const sessionManager = getAdminSessionManager();
  
  // Simple state management for authentication

  // Function to set form activity state
  const setFormActive = useCallback((active: boolean) => {
    setIsFormActive(active);
    if (active) {
      updateLastActivity();
    }
  }, []);

  // Function to load and update session configuration
  const loadSessionConfig = useCallback(async () => {
    try {
      const newConfig = await getSessionConfig();
      setSessionConfig(newConfig);
      
      if (config.enableDebugLogging) {
        console.log('🔄 Session config updated:', {
          sessionTimeout: `${newConfig.sessionTimeout / (60 * 60 * 1000)} hours`,
          warningThreshold: `${newConfig.warningThreshold / (60 * 1000)} minutes`,
          isActive: newConfig.isActive
        });
      }
    } catch (error) {
      console.warn('Failed to load session config, using cached/fallback:', error);
      setSessionConfig(getCachedSessionConfig());
    }
  }, []);

  const logout = useCallback(async (): Promise<boolean> => {
    // Prevent multiple logout attempts
    if (authState === 'logging_out') return false;
    
    setAuthState('logging_out');
    
    try {
      // Immediate local cleanup for better UX
      setUser(null);
      clearSessionData();
      localStorage.removeItem('sessionRestorationAttempted');
      localStorage.setItem('userLoggedOut', 'true');
      setIsBackendOffline(false);
      
      // Single server logout attempt (401 is expected if session already expired)
      try {
        const response = await authApi.logout();
        if (response?.statusCode === 200) {
          console.log('Server logout successful');
        }
      } catch (error: any) {
        // 401 is expected when session is already invalid - this is actually success
        if (error?.statusCode === 401 || error?.response?.status === 401) {
          console.log('Server logout successful (session already invalid)');
        } else {
          console.warn('Server logout failed:', error);
        }
      }
      
      // Single cookie cleanup attempt (401 is expected)
      try {
        await forceClearAuthCookies(config.apiUrl, config.endpoints.admin.logout, 1);
      } catch (error) {
        console.log('Cookie cleanup completed (some failures expected)');
      }
      
      // Notify other tabs about logout
      sessionManager.notifyLogout();
      
      // Redirect to login
      if (typeof window !== 'undefined') {
        window.location.href = ADMIN_LOGIN_PATH;
      }
      
      return true;
    } catch (error) {
      console.error('Logout process failed:', error);
      return false;
    } finally {
      setAuthState('idle');
      setIsLoading(false);
    }
  }, [authState]);

  
  // Handle auth invalidation from middleware with grace period
  useEffect(() => {
    const unsubscribe = onAuthInvalid(() => {
      console.log('Auth invalidation triggered, starting grace period...');
      
      // Set a flag to indicate we're in grace period
      localStorage.setItem('authGracePeriod', 'true');
      localStorage.setItem('gracePeriodStart', Date.now().toString());
      
      // Try to restore session once before forcing logout
      setTimeout(async () => {
        try {
          console.log('Grace period expired, attempting session restoration...');
          
          // Health check removed - proceed directly to session restoration
          
          // Try to refresh the session one more time
          const response = await fetch(`${config.apiUrl}${config.endpoints.admin.refresh}`, {
            method: 'POST',
            credentials: 'include',
          });
          
          if (response.ok) {
            console.log('Session restored during grace period');
            localStorage.removeItem('authGracePeriod');
            localStorage.removeItem('gracePeriodStart');
            toast.success('Session restored successfully');
            return;
          } else if (response.status === 401 || response.status === 403) {
            // Only logout on actual auth failures
            console.log('Session restoration failed with auth error during grace period');
            setUser(null);
            clearSessionData();
            localStorage.removeItem('sessionRestorationAttempted');
            localStorage.removeItem('authGracePeriod');
            localStorage.removeItem('gracePeriodStart');
            toast.error("Your session has expired. Please log in again.");
          } else {
            // Server errors (500, 502, etc.) - keep session and try again later
            console.log(`Session restoration failed with server error (${response.status}), keeping session`);
            localStorage.removeItem('authGracePeriod');
            localStorage.removeItem('gracePeriodStart');
            toast.warning("Server temporarily unavailable. Your session is preserved.");
          }
        } catch (error) {
          console.error('Session restoration error during grace period:', error);
          
          // Check if this is a network error vs auth error
          if (error instanceof Error && (
            error.message.includes('Failed to fetch') || 
            error.message.includes('websocket') || 
            error.message.includes('TransportError') ||
            error.message.includes('ERR_CONNECTION_REFUSED')
          )) {
            console.log('Network error during grace period, keeping session');
            // Don't clear session on network errors
            localStorage.removeItem('authGracePeriod');
            localStorage.removeItem('gracePeriodStart');
            toast.warning("Network error. Your session is preserved.");
            return;
          }
          
          // Only clear on actual auth failures - not on server errors
          console.log('Session restoration error during grace period, keeping session');
          localStorage.removeItem('authGracePeriod');
          localStorage.removeItem('gracePeriodStart');
          toast.warning("Server temporarily unavailable. Your session is preserved.");
        }
      }, 3000); // 3 second grace period
    });
    
    return () => {
      unsubscribe();
    };
  }, []);
  
  // Simple activity tracking - just update on login
  const extendSession = useCallback(() => {
    if (user) {
      updateLastActivity();
    }
  }, [user]);


  // Load session configuration on app startup (only when authenticated or on non-auth pages)
  useEffect(() => {
    // Skip loading config if we're on login page or user is not authenticated
    const isLoginPage = typeof window !== 'undefined' && isAdminLoginPath(window.location.pathname);
    
    if (isLoginPage) {
      // Use cached/fallback config on login page
      return;
    }
    
    // Only load config if user exists or we haven't attempted restoration yet
    if (user || !hasAttemptedRestoration) {
      loadSessionConfig();
    }
  }, [loadSessionConfig, user, hasAttemptedRestoration]);

  // Combined session status check and config refresh using Better Auth
  // Single interval that checks session status and refreshes config when needed
  useEffect(() => {
    if (!user) return;
    
    const checkInterval = setInterval(async () => {
      try {
        // Single call to session-status endpoint (reduces duplicate calls)
        const sessionStatus = await authApi.sessionStatus();
        
        if (!sessionStatus.isActive || !sessionStatus.user) {
          // Session expired - redirect to login
          setUser(null);
          clearSessionData();
          if (typeof window !== 'undefined') {
            window.location.href = ADMIN_LOGIN_PATH;
          }
          return;
        }
        
        // Silent update: refresh user data if changed (no loading state)
        if (sessionStatus.user) {
          setUser(sessionStatus.user);
        }
        
        // Refresh config only if cache expired (CONFIG_CACHE_DURATION = 5 minutes)
        // This prevents duplicate session-status calls
        if (shouldRefreshSessionConfig()) {
          loadSessionConfig();
        }
      } catch (error) {
        // Silent failure - don't show errors for background checks
        // Only log in debug mode
        if (config.enableDebugLogging) {
          console.error('Session status check failed:', error);
        }
        // Don't logout on network errors - keep existing session
      }
    }, sessionConfig.checkInterval);
    
    return () => clearInterval(checkInterval);
  }, [user, sessionConfig, loadSessionConfig]);

  // Simple session restoration on mount (one-time, guarded)
  useEffect(() => {
    // Skip restoration if on login page
    const isLoginPage = typeof window !== 'undefined' && isAdminLoginPath(window.location.pathname);
    
    if (isLoginPage) {
      setIsLoading(false);
      return;
    }
    
    // Clear stale grace period on app startup (if it's been more than 10 seconds)
    const gracePeriodStart = localStorage.getItem('gracePeriodStart');
    if (gracePeriodStart) {
      const timeSinceGracePeriod = Date.now() - parseInt(gracePeriodStart);
      if (timeSinceGracePeriod > 10000) { // 10 seconds
        console.log('Clearing stale grace period on app startup');
        localStorage.removeItem('authGracePeriod');
        localStorage.removeItem('gracePeriodStart');
      }
    }
    
    // For development: Clear grace period if it's been more than 5 seconds
    if (localStorage.getItem('authGracePeriod') === 'true') {
      const startTime = localStorage.getItem('gracePeriodStart');
      if (startTime) {
        const timeSinceStart = Date.now() - parseInt(startTime);
        if (timeSinceStart > 5000) { // 5 seconds
          console.log('Development: Clearing grace period after 5 seconds');
          localStorage.removeItem('authGracePeriod');
          localStorage.removeItem('gracePeriodStart');
        }
      } else {
        // No start time, clear it immediately
        console.log('Development: Clearing grace period with no start time');
        localStorage.removeItem('authGracePeriod');
      }
    }
    
    // Don't restore if user explicitly logged out
    if (localStorage.getItem('userLoggedOut') === 'true') {
      setIsLoading(false);
      return;
    }
    
    // Don't restore if already restoring or already attempted
    if (authState === 'restoring' || hasAttemptedRestoration) return;
    
    // Check if we're in grace period - wait before attempting restoration
    if (localStorage.getItem('authGracePeriod') === 'true') {
      console.log('In grace period, waiting before session restoration...');
      // Set a timeout to clear the grace period if it's been too long
      setTimeout(() => {
        if (localStorage.getItem('authGracePeriod') === 'true') {
          console.log('Grace period timeout, clearing and attempting restoration');
          localStorage.removeItem('authGracePeriod');
          localStorage.removeItem('gracePeriodStart');
          // Trigger a re-render to attempt restoration
          setHasAttemptedRestoration(false);
        }
      }, 5000); // 5 second timeout for grace period
      setIsLoading(false);
      return;
    }
    
    // Check if we've been rate limited recently - prevent immediate retries
    if (rateLimitManager.isRateLimited()) {
      const timeUntilReset = rateLimitManager.getTimeUntilReset();
      console.log(`Rate limited recently, waiting ${Math.ceil(timeUntilReset / 1000)}s before retry...`);
      setIsLoading(false);
      return;
    }
    
    // No longer using localStorage for user data - always check server
    
    const restoreSession = async () => {
      setAuthState('restoring');
      setHasAttemptedRestoration(true);
      
      // Skip restoration if already on login page
      const isLoginPage = typeof window !== 'undefined' && isAdminLoginPath(window.location.pathname);
      
      if (isLoginPage) {
        setIsLoading(false);
        setAuthState('idle');
        return;
      }
      
      try {
        // Check session status using Better Auth endpoint
        const sessionStatus = await authApi.sessionStatus();

        if (sessionStatus.isActive && sessionStatus.user) {
          setUser(sessionStatus.user);
          updateLastActivity();
          localStorage.removeItem('sessionRestorationAttempted');
          rateLimitManager.clearRateLimit();
          setIsLoading(false);
          setAuthState('idle');
          return;
        } else {
          // Session not active
          setUser(null);
          clearSessionData();
          localStorage.setItem('sessionRestorationAttempted', 'true');
        }

        // Handle rate limiting special-case separately
        // Note: validateAdminSession already avoids loops; this block remains for safety
        const response = { status: 200 } as any;
        if (response.status === 429) {
          // Handle rate limiting - stop trying to restore session
          console.warn('Rate limited during session restoration. Stopping retry attempts.');
          rateLimitManager.setRateLimited();
          localStorage.setItem('sessionRestorationAttempted', 'true');
          
          // Set user to null to prevent further auth checks
          setUser(null);
          clearSessionData();
          
          // Show user-friendly message
          toast.error('Too many authentication attempts. Please wait a moment and refresh the page.');
        }
      } catch (error) {
        console.error('Session restoration failed:', error);
        
        // Check if this is a network error - don't clear session immediately
        if (error instanceof Error && error.message.includes('Failed to fetch')) {
          console.log('Network error during session restoration, keeping existing session');
          // Keep existing session on network errors - don't clear user state
          // The user will remain authenticated until network is restored
        } else {
          // Clear session on other errors
          setUser(null);
          clearSessionData();
        }
      } finally {
        setIsLoading(false);
        setAuthState('idle');
      }
    };
    
    restoreSession();
  }, [authState]);

  // Backend health monitoring disabled - removed constant health checks

  const completeAuthenticatedLogin = useCallback(async (successMessage?: string) => {
    const sessionStatus = await authApi.sessionStatus();

    if (sessionStatus.isActive && sessionStatus.user) {
      setUser(sessionStatus.user);
      localStorage.removeItem('userLoggedOut');
      updateLastActivity();
      toast.success(successMessage || 'Successfully logged in!');
      return true;
    }

    toast.error('Login failed - session not established');
    return false;
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<AdminLoginResult> => {
    if (authState === 'logging_in') return { status: 'error' };

    setAuthState('logging_in');

    try {
      const response = await authApi.login(email, password);

      if (response?.statusCode === 200 && response.data?.requires2FA && response.data.challengeId) {
        return {
          status: 'requires2fa',
          challengeId: response.data.challengeId,
          maskedEmail: response.data.maskedEmail || email,
          expiresInMinutes: response.data.expiresInMinutes || 15,
        };
      }

      if (response && response.statusCode === 200 && response.data) {
        const ok = await completeAuthenticatedLogin(response.message);
        return ok ? { status: 'success' } : { status: 'error' };
      }

      toast.error(response?.message || 'Login failed');
      return { status: 'error' };
    } catch (error: unknown) {
      console.error('Login error:', error);
      const errorMessage =
        (error as { message?: string })?.message ||
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Login failed. Please try again.';
      toast.error(errorMessage);
      return { status: 'error' };
    } finally {
      setAuthState('idle');
    }
  }, [authState, completeAuthenticatedLogin]);

  const verifyLogin2fa = useCallback(async (challengeId: string, code: string): Promise<boolean> => {
    if (authState === 'logging_in') return false;

    setAuthState('logging_in');

    try {
      const response = await authApi.verifyLogin2fa(challengeId, code);

      if (response?.statusCode === 200) {
        return completeAuthenticatedLogin(response.message);
      }

      toast.error(response?.message || 'Verification failed');
      return false;
    } catch (error: unknown) {
      console.error('2FA verification error:', error);
      const errorMessage =
        (error as { message?: string })?.message ||
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Verification failed. Please try again.';
      toast.error(errorMessage);
      return false;
    } finally {
      setAuthState('idle');
    }
  }, [authState, completeAuthenticatedLogin]);

  const resendLogin2faCode = useCallback(async (challengeId: string) => {
    try {
      const response = await authApi.resendLogin2faCode(challengeId);
      if (response?.statusCode === 200 && response.data) {
        toast.success(response.message || 'A new code has been sent');
        return response.data;
      }
      toast.error(response?.message || 'Could not resend code');
      return null;
    } catch (error: unknown) {
      const errorMessage =
        (error as { message?: string })?.message || 'Could not resend code. Please try again.';
      toast.error(errorMessage);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        verifyLogin2fa,
        resendLogin2faCode,
        logout,
        extendSession,
        isLoading,
        isBackendOffline,
        isFormActive,
        setFormActive,
        sessionConfig,
        loadSessionConfig
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}; 